import { useCallback, useEffect, useReducer, useRef } from 'react';

import { getAccessToken } from '@/lib/api/axios';
import { getEnv } from '@/lib/env';
import { ReplayLiveAudioPlayer } from '@/lib/replay-live-audio';

// ── Backend contract types ──────────────────────────────────────────────────
// The call-monitor gateway at /listen-ws/<jwt> sends JSON messages with these
// shapes for voice replay live-listening.

interface AudioFrameMessage {
  type: 'replay_audio_frame';
  testCaseId: string;
  callId: string;
  seq: number;
  at: number;
  agent: string | null;
  customer: string | null;
}

interface TranscriptTurnMessage {
  type: 'replay_transcript_turn';
  testCaseId: string;
  role: 'agent' | 'customer';
  text: string;
  at: number;
}

interface AudioEndedMessage {
  type: 'replay_audio_ended';
  testCaseId: string;
  reason: string;
}

interface ReadyMessage {
  type: 'ready';
}

type ReplayServerMessage =
  | AudioFrameMessage
  | TranscriptTurnMessage
  | AudioEndedMessage
  | ReadyMessage;

// ── Hook state ──────────────────────────────────────────────────────────────

export interface LiveTranscriptTurn {
  role: 'agent' | 'customer';
  text: string;
  at: number;
}

interface ReplayLiveState {
  connected: boolean;
  listening: boolean;
  ended: boolean;
  endedReason: string | null;
  turns: LiveTranscriptTurn[];
  latestAt: number;
}

type ReplayLiveAction =
  | { type: 'CONNECTED' }
  | { type: 'DISCONNECTED' }
  | { type: 'LISTENING' }
  | { type: 'TURN'; turn: LiveTranscriptTurn }
  | { type: 'ENDED'; reason: string }
  | { type: 'STOP' }
  | { type: 'FRAME_AT'; at: number };

export function replayLiveReducer(
  state: ReplayLiveState,
  action: ReplayLiveAction,
): ReplayLiveState {
  switch (action.type) {
    case 'CONNECTED':
      return { ...state, connected: true };
    case 'DISCONNECTED':
      return { ...state, connected: false, listening: false };
    case 'LISTENING':
      return { ...state, listening: true, ended: false, endedReason: null, turns: [] };
    case 'TURN': {
      const next = [...state.turns, action.turn].sort((a, b) => a.at - b.at);
      return {
        ...state,
        turns: next,
        latestAt: Math.max(state.latestAt, action.turn.at),
      };
    }
    case 'ENDED':
      return { ...state, ended: true, endedReason: action.reason, listening: false };
    case 'STOP':
      return { ...state, listening: false };
    case 'FRAME_AT':
      return { ...state, latestAt: Math.max(state.latestAt, action.at) };
    default:
      return state;
  }
}

const INITIAL_STATE: ReplayLiveState = {
  connected: false,
  listening: false,
  ended: false,
  endedReason: null,
  turns: [],
  latestAt: 0,
};

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 16000;

// ── URL helper (same pattern as useLiveCallMonitor / useCallListSocket) ─────

function buildListenWsUrl(): string | null {
  const token = getAccessToken();
  if (!token) return null;

  const apiUrl = getEnv('VITE_API_URL');
  const apiOrigin = new URL(apiUrl);
  const wsProtocol = apiOrigin.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${wsProtocol}//${apiOrigin.host}/listen-ws/${encodeURIComponent(token)}`;
}

// ── Hook ────────────────────────────────────────────────────────────────────

interface UseReplayLiveOptions {
  /** The replay/test-case ID to listen to. */
  testCaseId: string | undefined;
  /** Only attempt connection when the replay is in a listening-eligible state. */
  enabled: boolean;
}

interface UseReplayLiveReturn {
  connected: boolean;
  listening: boolean;
  ended: boolean;
  endedReason: string | null;
  turns: LiveTranscriptTurn[];
  latestAt: number;
  /** Start listening — must be called from a click handler for autoplay policy. */
  startListening: () => void;
  /** Stop listening and disconnect audio. */
  stopListening: () => void;
  /** Mute/unmute playback. */
  setMuted: (muted: boolean) => void;
  /** Current audio latency in ms. */
  latencyMs: () => number;
}

export function useReplayLive({ testCaseId, enabled }: UseReplayLiveOptions): UseReplayLiveReturn {
  const [state, dispatch] = useReducer(replayLiveReducer, INITIAL_STATE);
  const wsRef = useRef<WebSocket | null>(null);
  const playerRef = useRef<ReplayLiveAudioPlayer | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Connect WebSocket when enabled + testCaseId present
  useEffect(() => {
    if (!enabled || !testCaseId) return;

    function connect() {
      const wsUrl = buildListenWsUrl();
      if (!wsUrl) return;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        dispatch({ type: 'CONNECTED' });
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data as string) as ReplayServerMessage;

          if (message.type === 'ready') {
            // Join the replay room once the gateway signals ready
            ws.send(JSON.stringify({ type: 'join_replay', testCaseId }));
            return;
          }

          if (message.type === 'replay_audio_frame') {
            if (message.testCaseId !== testCaseId) return;
            playerRef.current?.pushFrame(message.seq, message.agent, message.customer, message.at);
            dispatch({ type: 'FRAME_AT', at: message.at });
            return;
          }

          if (message.type === 'replay_transcript_turn') {
            if (message.testCaseId !== testCaseId) return;
            dispatch({
              type: 'TURN',
              turn: { role: message.role, text: message.text, at: message.at },
            });
            return;
          }

          if (message.type === 'replay_audio_ended') {
            if (message.testCaseId !== testCaseId) return;
            dispatch({ type: 'ENDED', reason: message.reason });
            playerRef.current?.stop();
            playerRef.current = null;
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        wsRef.current = null;

        // Stop the player before dispatching DISCONNECTED so the
        // AudioContext is never orphaned across reconnects.
        playerRef.current?.stop();
        playerRef.current = null;

        dispatch({ type: 'DISCONNECTED' });

        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(
            BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
            MAX_RECONNECT_DELAY,
          );
          reconnectAttemptsRef.current++;
          reconnectTimerRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    connect();

    return () => {
      clearTimeout(reconnectTimerRef.current);

      const ws = wsRef.current;
      if (ws) {
        if (ws.readyState === WebSocket.OPEN && testCaseId) {
          ws.send(JSON.stringify({ type: 'leave_replay', testCaseId }));
        }
        ws.onclose = null;
        ws.close();
        wsRef.current = null;
      }

      playerRef.current?.stop();
      playerRef.current = null;
    };
  }, [enabled, testCaseId]);

  const startListening = useCallback(() => {
    if (state.listening) return;
    const player = new ReplayLiveAudioPlayer();
    player.start();
    playerRef.current = player;
    dispatch({ type: 'LISTENING' });
  }, [state.listening]);

  const stopListening = useCallback(() => {
    playerRef.current?.stop();
    playerRef.current = null;
    dispatch({ type: 'STOP' });
  }, []);

  const setMuted = useCallback((muted: boolean) => {
    playerRef.current?.setMuted(muted);
  }, []);

  const latencyMs = useCallback(() => {
    return playerRef.current?.latencyMs() ?? 0;
  }, []);

  // When the replay leaves an active status externally, stop listening
  useEffect(() => {
    if (!enabled && state.listening) {
      playerRef.current?.stop();
      playerRef.current = null;
      dispatch({ type: 'STOP' });
    }
  }, [enabled, state.listening]);

  return {
    connected: state.connected,
    listening: state.listening,
    ended: state.ended,
    endedReason: state.endedReason,
    turns: state.turns,
    latestAt: state.latestAt,
    startListening,
    stopListening,
    setMuted,
    latencyMs,
  };
}
